import PageHero from '@/components/PageHero';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Lightbulb, Heart, Star, Calendar, Users, ArrowRight } from 'lucide-react';

const Lebenswerkstaetten = () => {
  const workshops = [
    {
      title: "Bewusstseinserweiterung",
      description: "Workshops zur Entwicklung des spirituellen Bewusstseins",
      icon: Lightbulb,
      topics: ["Meditation & Achtsamkeit", "Chakra-Arbeit", "Energiewahrnehmung"],
      duration: "1-2 Tage"
    },
    {
      title: "Emotionale Heilung",
      description: "Heilung alter Wunden und emotionaler Blockaden",
      icon: Heart,
      topics: ["Inneres Kind", "Traumaheilung", "Vergebungsarbeit"],
      duration: "Wochenende"
    },
    {
      title: "Spirituelle Praxis",
      description: "Vertiefung der spirituellen Praxis im Alltag",
      icon: Star,
      topics: ["Rituale & Zeremonien", "Manifestation", "Spirituelle Führung"],
      duration: "1-3 Tage"
    }
  ];

  return (
    <>
      <PageHero 
        title="Lebenswerkstätten" 
        description="Workshops für ganzheitliche Entwicklung und spirituelles Wachstum"
        image="https://images.unsplash.com/photo-1506905925346-21bda4d32df4"
      />
      
      <section className="py-20 section-bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Introduction */}
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-deep mb-6">
                Unsere Lebenswerkstätten
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Entdecken Sie transformative Workshops, die Ihnen helfen, Ihr volles 
                Potenzial zu entfalten und ein bewusstes, erfülltes Leben zu führen.
              </p>
            </div>

            {/* Workshop Categories */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {workshops.map((workshop, index) => (
                <GlassCard key={index} className="p-8">
                  <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-4">
                    <workshop.icon className="h-8 w-8 text-primary-deep" />
                  </div>
                  <h3 className="text-xl font-bold text-primary-deep mb-4 text-center">
                    {workshop.title}
                  </h3>
                  <p className="text-gray-700 mb-6 text-center">
                    {workshop.description}
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <h4 className="font-semibold text-gray-800">Themen:</h4>
                    {workshop.topics.map((topic, idx) => (
                      <div key={idx} className="flex items-center text-gray-600">
                        <span className="w-2 h-2 bg-primary-main rounded-full mr-3"></span>
                        {topic}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-center text-primary-main font-medium">
                    <Calendar className="h-4 w-4 mr-2" />
                    {workshop.duration}
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* Benefits and Format */}
            <div className="grid lg:grid-cols-2 gap-8 mb-16">
              <GlassCard className="p-8">
                <h3 className="text-2xl font-bold text-primary-deep mb-6">
                  Was Sie erwartet
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Praktische Übungen und Meditationen
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Tiefe Einsichten in spirituelle Zusammenhänge
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Austausch in einer vertrauensvollen Gruppe
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Werkzeuge für den spirituellen Alltag
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-main rounded-full mr-3 mt-2 shrink-0"></span>
                    Persönliche Transformation und Heilung
                  </li>
                </ul>
              </GlassCard>

              <GlassCard className="p-8">
                <h3 className="text-2xl font-bold text-primary-deep mb-6">
                  Format & Teilnahme
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-primary-main mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Kleine Gruppen</h4>
                      <p className="text-gray-600">Maximal 12 Teilnehmer für intensive Betreuung</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Calendar className="h-5 w-5 text-primary-main mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Flexible Termine</h4>
                      <p className="text-gray-600">Wochenend-Workshops und mehrtägige Retreats</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Heart className="h-5 w-5 text-primary-main mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Für alle Level</h4>
                      <p className="text-gray-600">Anfänger und Fortgeschrittene willkommen</p>
                    </div>
                  </div>
                </div>
              </GlassCard>
            </div>

            {/* Call to Action */}
            <GlassCard className="p-8 text-center">
              <h3 className="text-2xl font-bold text-primary-deep mb-4">
                Bereit für Ihre Transformation?
              </h3>
              <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                Melden Sie sich für unsere nächsten Lebenswerkstätten an und beginnen Sie 
                Ihre Reise zu einem bewussteren, erfüllteren Leben.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary-main hover:bg-primary-deep">
                  <a href="mailto:sabrina.kure@yahoo.com">
                    <Calendar className="mr-2 h-5 w-5" />
                    Workshop anfragen
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-primary-main text-primary-main hover:bg-primary-main hover:text-white">
                  <Link to="/anmeldung">
                    Mehr erfahren
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>
    </>
  );
};

export default Lebenswerkstaetten;